import 'core-js';

import Url from 'url';
import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/browser';
import Plausible from 'plausible-tracker';

import { Flex, Box } from 'reflexbox';

import 'shared/axiosRetry';
import SharedConfig from 'shared/config';
import { setVersions, preload as loadGameData } from 'shared/gameData';

import config from 'frontend/config';
import { retry, dlog, isBetaSite } from 'frontend/util';
import { familyFingerprint } from 'frontend/sentryFingerprints';
import { isForeignError } from 'frontend/sentryForeignErrors';
import { isStackExhaustion, summarizeStack } from 'frontend/sentryStackExhaustion';
import { isChunkEvalFamily, collectBundleProvenance } from 'frontend/sentryBundleProvenance';


import DefaultErrorBoundary, { DefaultError } from 'frontend/components/DefaultErrorBoundary';
import RaidbotsSkull from 'frontend/components/RaidbotsSkull';

const docElementTranslated = () => !!document.documentElement.className.match('translated');

let isGoogleTranslated = docElementTranslated();

const observer = new MutationObserver(() => {
  if (docElementTranslated()) {
    // eslint-disable-next-line no-console
    console.log('Google Translate detected, disabling error reporting');
    isGoogleTranslated = true;
  }
});

observer.observe(document.documentElement, {
  attributes: true,
  attributeFilter: ['class'],
  childList: false,
  characterData: false,
});

Sentry.init({
  dsn: 'https://7b5eb09d382745929edd40ca028764bf@sentry.io/123015',
  environment: process.env.NODE_ENV || 'development',
  release: `frontend-${process.env.VERSION}`,
  integrations: [
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      blockSelector: 'iframe[src*="wowhead"]',
    }),

    Sentry.extraErrorDataIntegration,
  ],
  beforeSend: (event, hint) => {
    // Never send events from development
    if (process.env.NODE_ENV !== 'production') {
      return null;
    }

    // ignore google translate page loads
    if (isGoogleTranslated) {
      return null;
    }

    // ignore anything that looks like a browser extension in the raw stack
    if (hint?.originalException?.stack?.match(
      /chrome-extension:|moz-extension:|safari-extension:|safari-web-extension:|sevendata\.fun/,
    )) {
      return null;
    }

    const exceptionValues = event.exception?.values || [];
    const frames = exceptionValues.flatMap(
      v => v.stacktrace?.frames || [],
    );

    // Chinese ISP/proxy injects a malware bundle (/suv4/rMainB.bundle.js, host
    // varies: vvipquan.com etc.) served same-origin, so its frames carry
    // raidbots.com URLs and masquerade as first-party — slipping past the
    // injected-fetch and no-Raidbots-frame guards below. denyUrls also misses it
    // because it only inspects the top frame (Sentry's fetch instrument). Match
    // the bundle path on any frame instead. (FRONTEND-8BZ)
    if (frames.some(f => /\/suv4\/rMainB\.bundle/.test(f.filename || ''))) {
      return null;
    }

    // Safari occasionally reports stackless promise rejections where the rejected value is the
    // browser's unhandledrejection CustomEvent itself. There is no actionable app stack attached.
    const originalException = hint?.originalException;
    const isDomEvent = originalException && (
      (window.Event && originalException instanceof window.Event)
      || /^\[object (Custom)?Event\]$/.test(Object.prototype.toString.call(originalException))
    );
    if (isDomEvent && exceptionValues.some(v => (
      v.mechanism?.type === 'auto.browser.global_handlers.onunhandledrejection'
      && v.type === 'CustomEvent'
      && frames.length === 0
    ))) {
      return null;
    }

    if (exceptionValues.some(v => (
      v.mechanism?.type === 'auto.browser.global_handlers.onunhandledrejection'
      && v.type === 'UnhandledRejection'
      && v.value === 'Object captured as promise rejection with keys: [object has no keys]'
      && frames.length === 0
    ))) {
      return null;
    }

    // Clipboard writes are denied by the browser when there's no permission or
    // active user gesture (common on mobile Firefox). Source call sites catch
    // this, but the react-copy-to-clipboard library and other paths can still
    // surface it as an unhandled rejection — drop it, it's not actionable.
    // (FRONTEND-8T7)
    if (exceptionValues.some(v => (
      v.type === 'NotAllowedError'
      && /clipboard/i.test(v.value || '')
    ))) {
      return null;
    }

    const isSentryWrapperFrame = f => (
      f.function === 'sentryWrapped'
      || f.filename?.includes('/@sentry/')
      || f.filename?.includes('/node_modules/@sentry/')
    );
    if (exceptionValues.some(v => (
      v.mechanism?.type === 'auto.browser.global_handlers.onunhandledrejection'
      && v.type === 'TypeError'
      && /^Load failed(?: \([^)]+\))?$/.test(v.value || '')
      && frames.length > 0
      && frames.every(isSentryWrapperFrame)
    ))) {
      return null;
    }

    // "Failed to fetch (host)" rejections from extension-injected or third-party
    // scripts (gadstat.com tracker, ad pixels, etc). Sentry's fetch instrumentation
    // is bundled into raidbots.com/*.js so the throw site is inside our bundle —
    // the auto.browser.* throw-site check below can't tell injected from real by
    // URL alone. Real raidbots fetches always have a caller frame inside our .js;
    // injected scripts produce only <anonymous> or document-URL frames.
    const isInjectedFetchRejection = exceptionValues.some(v => (
      v.mechanism?.type === 'auto.browser.global_handlers.onunhandledrejection'
      && v.type === 'TypeError'
      && /^Failed to fetch \(/.test(v.value || '')
    ));
    if (isInjectedFetchRejection && frames.length > 0) {
      const isRaidbotsJsFrame = f => (
        f.filename
        && /raidbots\.com\/.*\.js/.test(f.filename)
        && !/^blob:/.test(f.filename)
      );
      // Sentry's fetch instrumentation and addEventListener wrapper are bundled
      // into our .js, so every fetch rejection carries raidbots.com frames (the
      // throw site is @sentry/.../fetch.js; frame[0] is sentryWrapped). A real
      // app fetch also has a non-Sentry raidbots caller frame; injected scripts
      // (suv4/rMainB.bundle, vvipquan, etc.) do not. Ignore the Sentry glue.
      const callerFrames = frames.slice(0, -1);
      if (!callerFrames.some(f => isRaidbotsJsFrame(f) && !isSentryWrapperFrame(f))) {
        return null;
      }
    }

    // React's internal "Should not already be working." scheduler invariant —
    // thrown from react-dom/scheduler (commonly re-entered via the core-js
    // setImmediate polyfill or a browser extension), never by app code. Two
    // exact-match filters in a row failed to catch every variant (the path
    // based one never matched; the type+value one still let events from
    // current releases through — FRONTEND-8NK/8NS), so match the React-unique
    // message as a substring, regardless of exception type or prefix.
    if (exceptionValues.some(v => (v.value || '').includes('Should not already be working'))) {
      return null;
    }

    // ignore Stripe.js load failures (script blockers)
    if (exceptionValues.some(v => v.value?.includes('Failed to load Stripe.js'))) {
      return null;
    }

    // Extensions, injected proxy scripts, and third-party XHR wrappers throwing
    // inside listeners Sentry globally wrapped. Unfixable from here, so drop them
    // at capture instead of leaving them unresolved. (FRONTEND-994/99B/8R2/8RG)
    if (isForeignError(exceptionValues, originalException)) {
      return null;
    }

    // ignore Cypress test stub errors that leak via captureMessage
    if (event.message?.includes('test_stub_intercepted')) {
      return null;
    }

    // Drop events where no stack frame comes from Raidbots code.
    // This catches extension errors, injected scripts, and other third-party noise
    // that slip past allowUrls (e.g. errors with no URL or inline scripts).
    if (frames && frames.length > 0) {
      const isRaidbotsFrame = f => f.filename && /raidbots\.com\/.*\.js/.test(f.filename) && !/^blob:/.test(f.filename);

      // Sentry's auto-instrumentation (browserApiErrors, global_handlers, fetch)
      // globally wraps addEventListener, setTimeout, fetch, etc. These wrappers
      // are bundled into our main.js, so when an injected/extension script throws,
      // the resulting stack carries raidbots.com frames at the bottom of the call
      // stack and the "any raidbots frame" check passes through third-party noise.
      //
      // Sentry orders frames chronologically — frames[0] is the oldest call (where
      // the wrapper sits), frames[length-1] is the throw site. For all auto.browser
      // mechanisms, require the throw site itself to be raidbots code instead of
      // just any frame on the stack.
      const mechanism = event.exception?.values?.[0]?.mechanism?.type;
      if (mechanism && mechanism.startsWith('auto.browser.')) {
        const throwSite = frames[frames.length - 1];
        if (!throwSite || !isRaidbotsFrame(throwSite)) {
          return null;
        }
      } else if (!frames.some(isRaidbotsFrame)) {
        return null;
      }
    }

    const errorMessage = hint?.originalException?.message ?? exceptionValues[0]?.value;

    // Known error families whose stack-based grouping rotates every release —
    // pin them to a stable fingerprint so archiving them once sticks.
    const familyKey = familyFingerprint(errorMessage);
    if (familyKey) {
      event.fingerprint = [familyKey]; // eslint-disable-line no-param-reassign
    }

    // FRONTEND-996/997 throw inside __webpack_require__.d while a lazy chunk
    // evaluates. Every remaining hypothesis turns on whether the page had
    // assets from more than one build, so record what it actually fetched.
    // mixedBuilds rides a tag because tags are indexed and extras are not —
    // the next sweep should be able to aggregate it, not read events one by one.
    if (isChunkEvalFamily(errorMessage)) {
      const provenance = collectBundleProvenance();
      /* eslint-disable no-param-reassign */
      event.tags = { ...event.tags, bundle_mixed_builds: String(provenance.mixedBuilds) };
      event.extra = { ...event.extra, bundleProvenance: provenance };
      /* eslint-enable no-param-reassign */
    }

    // Stack-exhaustion errors report a short, non-repeating stack (FRONTEND-8NJ's
    // is ~12 frames) — which cannot be the whole story, since you can't exhaust a
    // stack in 12 frames. Capture what the parsed stacktrace can't tell us: how
    // many frames the RAW string held, and whether any frame repeats. A genuine
    // runaway recursion repeats one frame many times; an incidental trip doesn't.
    // Tags carry the aggregatable parts (tags are indexed, extras are not).
    if (isStackExhaustion(errorMessage)) {
      const stackSummary = summarizeStack(originalException?.stack);
      /* eslint-disable no-param-reassign */
      event.tags = {
        ...event.tags,
        stack_frame_count: String(stackSummary.frameCount),
        stack_max_repeat: String(stackSummary.maxRepeat),
        stack_distinct_frames: String(stackSummary.distinctFrames),
      };
      event.extra = { ...event.extra, stackExhaustion: stackSummary };
      /* eslint-enable no-param-reassign */
    }

    // Axios failures capture with only axios-internal frames, so status-code
    // buckets (e.g. the FRONTEND-8TJ 400s) say nothing about which request
    // failed. Tag the request so the events are attributable.
    if (originalException?.isAxiosError) {
      // eslint-disable-next-line no-param-reassign
      event.tags = {
        ...event.tags,
        axios_url: originalException.config?.url,
        axios_method: originalException.config?.method,
        axios_status: originalException.response?.status ?? originalException.code ?? 'no-response',
      };
    }

    return event;
  },
  allowUrls: [
    /https?:\/\/((ulduar|beta|www|mimiron)\.)?raidbots\.com/,
  ],
  includePaths: [
    /https?:\/\/((ulduar|beta|www|mimiron)\.)?raidbots\.com/,
  ],

  // only capture replay on error
  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: 1.0,

  ignoreErrors: [
    // google translate extension error
    'a[b].target.className.indexOf is not a function',

    // misc errors that are probably extensions, weird browser versions, or non-fatal edge cases
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications.',
    'NotFoundError: Failed to execute \'removeChild\' on \'Node\': The node to be removed is not a child of this node.',
    'Unexpected end of input',
    'hideMyLocation',
    'Unexpected token \'else\'',
    'Unexpected end of input',
    'The node before which the new node is to be inserted is not a child of this node.',
    'The node to be removed is not a child of this node',
    // Same DOM-mutation race as above (browser auto-translation reparents nodes
    // under React), but Firefox/Safari phrase the DOMException differently.
    'Child to insert before is not a child of this node', // Firefox insertBefore
    'The object can not be found here.', // Safari removeChild/insertBefore
    'can\'t redefine non-configurable property "userAgent"',
    'Should not already be working.',
    'adb_rotate?', // some .ru domain XHR, probably from an extension
    'undefined is not an object (evaluating \'webkit.messageHandlers.loginsManagerMessageHandler.postMessage\')',
    'Cannot redefine property: BetterJsPop',
    'Failed to execute \'replaceState\' on \'History\'',
    'Cannot redefine property: googletag',
    // microsoft crawler triggering a recursive error?
    'Non-Error promise rejection captured with value: Object Not Found Matching Id',
    // stackless unhandled rejections (Promise.reject() with no value); no frontend code paths produce this,
    // so matches are extension/injected-script noise that slips past the no-Raidbots-frame filter
    'Non-Error promise rejection captured with value: undefined',
    // browser autoplay policy — extensions/embeds calling HTMLMediaElement.play() without a user gesture;
    // Raidbots frontend has no audio/video playback code
    'The play method is not allowed by the user agent',
    // browser extensions (MetaMask/wallets, password managers) and Web Crypto API key lookups;
    // arrives stackless so the no-Raidbots-frame filter can't catch it
    'Key not found',
    // Sentry wrapper for promise rejections from MetaMask/Web3 wallets and chrome.runtime
    // extensions (EIP-1474 { code, message } shape); Raidbots code throws Error instances
    'Object captured as promise rejection with keys: code, message',
    'window.bannerNight is not a function',
    '$ is not defined',
    'www-raidbots-com.translate.goog',
    'chrome-extension://',
    'sevendata.fun',
    // some random safari extension
    'Error response received for message <page-ready>',

    // List of common ignores from https://docs.sentry.io/clients/javascript/tips/

    // Random plugins/extensions
    'top.GLOBALS',
    // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
    'originalCreateNotification',
    'canvas.contentDocument',
    'MyApp_RemoveAllHighlights',
    'http://tt.epicplay.com',
    'Can\'t find variable: ZiteReader',
    'jigsaw is not defined',
    'ComboSearch is not defined',
    'http://loading.retry.widdit.com/',
    'atomicFindClose',
    // Facebook borked
    'fb_xd_fragment',
    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
    // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
    'bmi_SafeAddOnload',
    'EBCallBackMessageReceived',
    // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
    'conduitPage',
    // Generic error code from errors outside the security sandbox
    // You can delete this if using raven.js > 1.0, which ignores these automatically.
    'Script error.',
    // Avast extension error
    '_avast_submit',
    'Identifier \'change_ua\' has already been declared',

    // Sincera crawler
    'apstagLOADED is not defined',
    '__cmp is not defined',
    'googlefc is not defined',
    'moat_px is not defined',

    'navigator.geolocation is undefined',

    'Cannot read properties of undefined (reading \'mvpConfig\')',
    'YT is not defined',
    'dailymotion is not defined',
    'jwDefaults is not defined',
    'IMUID is not defined',

    // from OrangeMonkey extension
    'reading \'firefoxSample\'',

    // Jan 2025 - tons of errors being repeatedly spammed and I can't determine the source
    'Illegal invocation',
    'getElementsByTagName',

    // react-redux v9 uses ??= which old browsers (Sogou Explorer etc.) don't support
    "Unexpected token '='",

    // Sentry Replay trying to access cross-origin iframes (e.g. Wowhead tooltips)
    "Blocked a frame with origin",

    // Browser extension trying to message a tab that no longer exists (common on mobile Safari)
    "Invalid call to runtime.sendMessage(). Tab not found.",

    // Browser extension content script messaging a background/service worker that's gone
    // (chrome.runtime). Arrives stackless via onunhandledrejection, so the no-Raidbots-frame
    // filter (guarded on frames.length > 0) can't catch it.
    'Could not establish connection. Receiving end does not exist.',

    // Browser extension messaging where a chrome.runtime listener returned true (async
    // response) but the port closed before responding. Pure extension noise — Raidbots
    // code uses no chrome.runtime messaging. Arrives stackless past the no-Raidbots-frame filter.
    'A listener indicated an asynchronous response by returning true, but the message channel closed',

    // Mobile Safari parse error from injected inline scripts (in-app browsers, content blockers,
    // extensions). Attributed to the document URL so it slips past the no-Raidbots-frame filter.
    // Our compiled bundle can't produce this — Webpack/Terser won't emit `--<non-lvalue>`, and a
    // real parse failure would break every Mobile Safari user, not isolated sessions.
    'The prefix-decrement operator requires a reference expression',

    // Chinese mobile-browser resource-sniffer (UC/Quark/QQ/Baidu) injects a CompareVersion_Utils
    // helper that calls .split() on a non-string. The `version1` parameter name comes from their
    // code, not ours (no matches in repo). Slips past the no-Raidbots-frame filter because Sentry's
    // addEventListener wrapper (`sentryWrapped`) is bundled into main.js, so every event has a
    // raidbots.com frame even when the actual fault is in injected <anonymous> script.
    'version1.split is not a function',

    // Content-marker / annotation extension (Firefox `markValues_s/<` frame). Uses an internal
    // property named "ODE" — no matches in our code. Same addEventListener-wrapper bypass of the
    // no-Raidbots-frame filter as the version1 entry above.
    'can\'t access property "ODE"',

    // old browser
    'crypto.randomUUID is not a function',
  ],
  denyUrls: [
    // Google Adsense
    /pagead\/js/i,
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Woopra flakiness
    /eatdifferent\.com\.woopra-ns\.com/i,
    /static\.woopra\.com\/js\/woopra\.js/i,
    // Browser extensions
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-extension:\/\//i,
    /^safari-web-extension:\/\//i,
    // Other plugins
    /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
    /webappstoolbarba\.texthelp\.com\//i,
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
    /vvipquan\.com/i,
    /\/suv4\/rMainB\.bundle\.js/i,
  ],
});

const plausible = Plausible({
  domain: 'raidbots.com',
});


const url = Url.parse(window.location.href, true);
const headless = Boolean(url.query.headless);

if (process.env.NODE_ENV === 'production') {
  // use testMode to avoid tracking puppeteer requests
  if (!headless) {
    plausible.enableAutoPageviews();
  }
}

class BootScreen extends React.Component {
  state = {
    show: false,
  };

  componentDidMount() {
    this.timeout = setTimeout(() => {
      this.setState({ show: true });
    }, 500);
  }

  componentWillUnmount() {
    clearTimeout(this.timeout);
  }

  render() {
    if (!this.state.show) {
      return null;
    }
    return (
      <Flex justify="center" align="center" column style={{ height: '100%' }}>
        <Box>
          <RaidbotsSkull fill="#444" />
        </Box>
        <Box
          style={{
            color: '#555',
            fontFamily: config.fontFamily,
            textAlign: 'center',
          }}
        >
          Loading...
        </Box>
      </Flex>
    );
  }
}

const RaidbotsApp = React.lazy(() => retry(() => import('./app')));
const LazyApp = () => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    const init = async () => {
      try {
        const version = window.config.gameDataVersion;
        const fallbackVersion = isBetaSite() ? SharedConfig.wowEnvMap.test : SharedConfig.wowEnvMap.live;
        dlog('Game data version:', version);
        const start = Date.now();
        setVersions(version, fallbackVersion);
        await loadGameData();
        const duration = Date.now() - start;
        dlog(`Game data loaded in ${duration.toLocaleString()}ms`);
        setLoading(false);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        // This does fail on occasion but there's not really anything I can do about it.
        // It's such critical path that if it fails for a large number of users I'll see other metric failures pretty
        // rapidly so having the error in Sentry just becomes noise that I can't address
        //Sentry.captureException(err);
        setError(err);
      }
    };
    init();
  }, []);

  if (error) {
    return (
      <DefaultError
        title="Unable to load game data"
        description="Raidbots was unable to load critical data files"
      />
    );
  }

  if (loading) {
    return (
      <BootScreen />
    );
  }

  return (
    <DefaultErrorBoundary>
      <React.Suspense fallback={<BootScreen />}>
        <RaidbotsApp />
      </React.Suspense>
    </DefaultErrorBoundary>
  );
};

const App = LazyApp;

const root = createRoot(document.getElementById('app'));
root.render(<App />);

window.raidbots = {
  version: process.env.VERSION,
  env: process.env.NODE_ENV,
};
